//Import statements
import Highcharts from "highcharts"
import highchartsStock from "highcharts/modules/stock"
highchartsStock(Highcharts)
import highchartsExporting from "highcharts/modules/exporting"
highchartsExporting(Highcharts)
import highchartsExportData from "highcharts/modules/export-data"
highchartsExportData(Highcharts)
import highchartsAccessibility from "highcharts/modules/accessibility"
highchartsAccessibility(Highcharts)
import highchartsNoData from "highcharts/modules/no-data-to-display"
highchartsNoData(Highcharts)
import highchartsMore from "highcharts/highcharts-more"
highchartsMore(Highcharts)
import highchartsGauge from "highcharts/modules/solid-gauge"
highchartsGauge(Highcharts)

import Webcomponent from "../common/WebcomponentCreator"
import colors from "./color"
import { fetchTrendData } from "./FetchFilterData"
const diagramColors = colors.allColors

class HighchartsBase extends Webcomponent {
    constructor(template) {
        super(template, { shadowMode: "closed" })
        this.chartOptions = {}
        this.series = []
        this.categories = []
        this.colors = diagramColors
        this.firstColors = []
        this.loading = false
        this.seriesStyleMap = {} // Store persistent color and symbol mapping for series
        this.usePersistentStyles = true // Flag to enable/disable persistent colors and symbols
        // Get valid marker symbols from Highcharts options
        this.availableSymbols = Highcharts.getOptions().symbols || [
            "circle",
            "square",
            "diamond",
            "triangle",
            "triangle-down"
        ]

        Highcharts.setOptions({
            exporting: {
                url: "https://highcharts.c.vantetider.se/"
            }
        })

        this.overrideHighchartsMethods()

        Highcharts.SVGRenderer.prototype.symbols.cross = function (x, y, w, h) {
            return [
                "M",
                x,
                y,
                "L",
                x + w,
                y + h,
                "M",
                x + w,
                y,
                "L",
                x,
                y + h,
                "z"
            ]
        }
    }

    // Adds the spinner methods to the showLoading/hideLoading methods.
    // Uses a static flag so the prototype is patched only once regardless
    // of how many HighchartsBase instances are created, preventing a
    // growing wrapper chain that called showSpinner N times per showLoading.
    overrideHighchartsMethods() {
        if (HighchartsBase._methodsOverridden) return
        HighchartsBase._methodsOverridden = true

        const originalShowLoading = Highcharts.Chart.prototype.showLoading
        const originalHideLoading = Highcharts.Chart.prototype.hideLoading

        Highcharts.Chart.prototype.showLoading = function () {
            originalShowLoading.apply(this, arguments)
            if (!this.spinner) {
                const spinner = document.createElement("spinner-component")
                this.container.appendChild(spinner)
                this.spinner = spinner
            }
            this.spinner.style.display = "block"
        }

        Highcharts.Chart.prototype.hideLoading = function () {
            originalHideLoading.apply(this, arguments)
            if (this.spinner) {
                this.spinner.style.display = "none"
            }
        }
    }

    createEmptyChart(chartDiv, chartOptions) {
        const options = {
            ...chartOptions,
            series: [
                {
                    name: "",
                    data: [null]
                }
            ]
        }
        this.chart = new Highcharts.Chart(chartDiv, options)
    }

    showEmptyChart(chartDiv, chartOptions) {
        this.createEmptyChart(chartDiv, chartOptions)
        this.chart.showLoading()
    }

    // Hook method for processing data
    processSeriesData(res) {}

    // Hook for components that need custom response-to-series mapping.
    transformResponseToSeries() {
        return null
    }

    getSerieStyle(serieName, index) {
        if (!this.usePersistentStyles) {
            return {
                color: this.colors[index % this.colors.length],
                symbol: undefined, // Let Highcharts use default
                legendIndex: undefined
            }
        }

        if (!this.seriesStyleMap[serieName]) {
            const usedStyles = Object.values(this.seriesStyleMap)

            // Assign next style based on how many series we already have
            const nextIndex = usedStyles.length
            const colorIndex = nextIndex % this.colors.length
            const symbolIndex = nextIndex % this.availableSymbols.length

            this.seriesStyleMap[serieName] = {
                color: this.colors[colorIndex],
                symbol: this.availableSymbols[symbolIndex],
                legendIndex: nextIndex // Track order for consistent legend
            }
        }

        return this.seriesStyleMap[serieName]
    }

    updateChart(
        params,
        url,
        defaultChartOptions,
        chartDiv = this.template,
        seriesObjectName = "series",
        chartPointMargin = null,
        hideParams = undefined
    ) {
        //Need loading here to avoid spam clicking
        if (this.loading) return
        this.loading = true

        if (this.chart) {
            this.chart.showLoading()
        }
        this.getData(params, url, seriesObjectName)
            .then(() => {
                this.setChartOptions(defaultChartOptions)
                if (chartPointMargin || chartPointMargin === 0) {
                    this.chartOptions.chart.height =
                        this.getDynamicHeight(chartPointMargin)
                }
                this.render(chartDiv)

                if (params && params.text) {
                    this.setSubtitle(
                        this.createSubtitleString(
                            params.text,
                            undefined,
                            hideParams
                        )
                    )
                }
                this.loading = false
                this.chart.hideLoading()
            })
            .catch(() => {
                this.loading = false
                this.chart.hideLoading()
            })
    }

    //Get data and adds it to this.series
    async getData(
        params,
        url,
        seriesObjectName = "series",
        serieNames = "serieNames",
        startAt = 0
    ) {
        this.series = []

        try {
            var res = await fetchTrendData(url, params.values, seriesObjectName)

            const transformedData = this.transformResponseToSeries(res, {
                seriesObjectName,
                serieNames,
                startAt
            })

            if (transformedData && transformedData.series) {
                this.categories = transformedData.categories || res.xNames || []
                this.series = transformedData.series
                this.processSeriesData(res)

                if (this.firstColors.length > 0) {
                    for (let i = 0; i < this.series.length; i++) {
                        if (this.series[i].data && this.series[i].data[0]) {
                            this.series[i].data[0].color = this.firstColors[i]
                        }
                    }
                }
                return true
            }

            this.categories = res.xNames
            for (let i = startAt; i < res[serieNames].length + startAt; i++) {
                //VERY temporary fix. AvailableHealthcare trend has a spelling error, this is a quick fix to solve it.
                if (res[seriesObjectName][i].name === "Telefontillgänglihet")
                    res[seriesObjectName][i].name = "Telefontillgänglighet"

                const serieName = res[seriesObjectName][i].name
                const serieStyle = this.getSerieStyle(serieName, i)

                this.series.push({
                    name: serieName,
                    data: res[seriesObjectName][i].data,
                    color: serieStyle.color,
                    marker: {
                        symbol: serieStyle.symbol
                    },
                    legendIndex: serieStyle.legendIndex,
                    zIndex: i
                })

                this.processSeriesData(res)
            }
            if (this.firstColors.length > 0) {
                for (let i = 0; i < this.series.length; i++) {
                    this.series[i].data[0].color = this.firstColors[i]
                }
            }
            return true
        } catch (err) {
            console.error("HighchartsBase.getData() error:", err)
            console.log(err)
            this.errorNoData()
            return false
        }
    }
    render(chartDiv) {
        if (this.chart !== undefined) {
            if (this.chartOptions.xAxis)
                this.chartOptions.xAxis.categories = this.categories
            this.chart.hideLoading()
            while (this.chart.series.length > 0) {
                this.chart.series[0].remove(false, false)
            }
            this.series.forEach((serie) => {
                this.chart.addSeries(serie, false)
            })

            this.chart.update(this.chartOptions)
        } else {
            if (this.chartOptions.xAxis)
                this.chartOptions.xAxis.categories = this.categories
            this.chartOptions.series = this.series
            this.chart = new Highcharts.Chart(chartDiv, this.chartOptions)
        }
        var evt = document.createEvent("HTMLEvents")
        evt.initEvent("resize", false, true)
        window.dispatchEvent(evt)
    }
    getDynamicHeight(
        setMargin = 0,
        pointWidth = this.chartOptions.plotOptions
            ? this.chartOptions.plotOptions.series.pointWidth
            : 30
    ) {
        let numbCats = 0
        this.series.forEach((s) => {
            if (s.data) numbCats += s.data.length
        })
        const catHeight = pointWidth
        const margin = setMargin
        return (catHeight + margin) * numbCats
    }
    trimUrlParams(params) {
        for (var property in params.values) {
            if (params.values.hasOwnProperty(property)) {
                if (params.values[property] === "-1") {
                    delete params.values[property]
                }
            }
        }
    }
    setChartOptions(options) {
        options = {
            ...options,
            lang: { ...options.lang },
            title: options.title ? { ...options.title } : options.title
        }
        if (this.series) {
            options.series = this.series
        } else if (this.chartOptions.series) {
            this.series = this.chartOptions.series
        }

        this.chartOptions = options
    }
    addSeries(label, seriesData, enabled, color) {
        this.series.push({
            name: label,
            data: seriesData,
            visible: enabled,
            zIndex: enabled ? 1 : 0,
            color: color
        })
    }
    addUnnamedSerie(seriesData) {
        this.series.push(seriesData)
    }
    setSubtitle(titleText) {
        if (this.chart != undefined) {
            this.chart.setTitle(null, { text: titleText, align: "left" })
        }
    }
    createSubtitleString(
        params,
        options = {
            monthAndYearAsTime: true,
            placeText: "Plats"
        },
        hideParams = { region: true, area: false }
    ) {
        for (const key in hideParams) {
            console.log(
                "Checking param for hiding:",
                key,
                "Hide:",
                hideParams[key],
                "Value:",
                params[key]
            )
            if (hideParams[key] == true && params[key]) {
                console.log("Hiding param:", key)
                delete params[key]
            }
        }
        //let result = "";
        let { monthAndYearAsTime, placeText } = options
        var production = "" //= params.production ? " <b>Status:</b> " + params.production : "";
        if (params.production) {
            production = "<b>" + params.production + "</b>"
            if (params.area) production += " <b> - " + params.area + "</b>"
            production += "<br>"
        }
        var indicatorText =
            this.chartOptions.title && this.chartOptions.title.text
                ? this.chartOptions.title.text
                : null
        if (indicatorText && this.chart != undefined) {
            this.chart.setTitle({ text: null })
        }
        const indicator = indicatorText
            ? "<b>Indikator:</b> " + indicatorText + " "
            : ""
        const place = params.region
            ? "<b>" + placeText + ":</b> " + params.region
            : ""
        let time
        if (hideParams.time) {
            time = ""
        } else if (monthAndYearAsTime)
            time =
                params.month && params.year
                    ? " <b>Tid:</b>  " + params.month + " " + params.year
                    : " "
        else
            time =
                params.month && params.year
                    ? " <b>Månad:</b>  " +
                      params.month +
                      " <b>År:</b>  " +
                      params.year
                    : " "
        const gender = params.gender ? " <b>Kön:</b> " + params.gender : " "
        const prof = params.profession
            ? " <b>Yrkesgrupp:</b> " + params.profession
            : ""
        const careType = params.careType
            ? " <b>Besöksform:</b> " + params.careType
            : ""
        const owner = params.management
            ? " <b>Ägarform:</b> " + params.management
            : ""
        const unit = params.unit ? " <b>Vårdcentral:</b> " + params.unit : ""
        const hospitalName = params.hospitalName
            ? " <b>Sjukhus:</b> " + params.hospitalName
            : ""
        const hospitalType = params.hospitalType
            ? " <b>Typ:</b> " + params.hospitalType
            : ""
        const area = params.area ? " <b>Område:</b> " + params.area : ""
        const mvo = params.mvo ? " <b>Specialitet:</b> " + params.mvo : ""
        const service = params.service
            ? " <b>Vårdutbud:</b> " + params.service
            : ""
        const ageGroup = params.ageGroup
            ? " <b>Åldersgrupp:</b> " + params.ageGroup
            : ""
        const healthcareprovider = params.healthcareprovider
            ? " <b>Vårdgivare:</b> " + params.healthcareprovider
            : ""
        const clinic = params.clinic ? " <b>Klinik:</b> " + params.clinic : ""

        return (
            production +
            indicator +
            place +
            unit +
            hospitalName +
            healthcareprovider +
            clinic +
            gender +
            ageGroup +
            prof +
            careType +
            owner +
            hospitalType +
            area +
            mvo +
            service +
            time
        )
    }
    errorNoData() {
        if (this.chart != undefined) {
            while (this.chart.series.length > 0) {
                this.chart.series[0].remove(false, false)
            }
            this.chart.redraw()
            this.chart.hideLoading()
            this.chart.showNoData()
        }
    }
}

export default HighchartsBase
